import type { ReactNode } from "react";

export interface DetailRow {
  label: string;
  value: ReactNode;
  action?: ReactNode;
}

export interface DetailGroup {
  title: string;
  rows: DetailRow[];
}

function Rows({ rows }: { rows: DetailRow[] }) {
  return (
    <dl className="overflow-hidden rounded-lg bg-surface">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex items-center justify-between gap-4 border-t border-muted/60 px-4 py-2.5 text-sm first:border-t-0"
        >
          <dt className="text-foreground/70">{row.label}</dt>
          <dd className="m-0 text-right text-foreground">
            {row.action ? (
              <div className="flex items-center justify-end gap-3">
                {row.value}
                {row.action}
              </div>
            ) : (
              row.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function DetailList({
  rows,
  groups,
}: {
  rows?: DetailRow[];
  groups?: DetailGroup[];
}) {
  if (groups) {
    return (
      <div className="space-y-5">
        {groups.map((group) => (
          <section key={group.title}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-foreground/70">
              {group.title}
            </h2>
            <Rows rows={group.rows} />
          </section>
        ))}
      </div>
    );
  }

  return <Rows rows={rows ?? []} />;
}

export function DetailListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="animate-pulse overflow-hidden rounded-lg bg-surface">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center justify-between gap-4 border-t border-muted/60 px-4 py-3 first:border-t-0"
        >
          <div className="h-3 w-24 rounded bg-muted" />
          <div className="h-3 w-32 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}
